import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type { SuperAdminUser, TenantMembership, AdminLoginResponse } from '@yawi/types';
import type { IAdminAuthRepository } from '@yawi/domain';
import { createWebStorageClient, hasWebSessionCookie, refreshSession, type StorageClient } from '@yawi/infrastructure';

interface AuthState {
  user: SuperAdminUser | null;
  tenants: TenantMembership[];
  currentTenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresTenantSelection: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  selectTenant: (tenantId: string) => Promise<void>;
  accessTenant: (tenantId: string) => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'yawi-admin-auth';

interface StoredAuth {
  user: SuperAdminUser | null;
  tenants: TenantMembership[];
  currentTenantId: string | null;
}

export function AuthProvider({
  children,
  authRepository,
  storageClient: storageClientProp,
}: {
  children: ReactNode;
  authRepository: IAdminAuthRepository;
  storageClient?: StorageClient;
}) {
  const storage = useMemo(
    () => storageClientProp ?? createWebStorageClient(),
    [storageClientProp],
  );

  const [state, setState] = useState<AuthState>({
    user: null,
    tenants: [],
    currentTenantId: null,
    isAuthenticated: false,
    isLoading: true,
    requiresTenantSelection: false,
  });

  // No confiamos en el usuario cacheado en storage sin revalidarlo contra el
  // backend primero. Antes, `isAuthenticated` se derivaba directo de
  // `!!parsed.user` — alcanzaba con escribir un `user` falso en localStorage
  // (a mano en DevTools, o vía un XSS) para que el router del cliente
  // mostrara el shell de admin sin haber pasado un login real. Ese objeto no
  // trae ningún token (el access token vive aparte, solo en memoria), así
  // que no otorgaba acceso a datos reales — pero igual era una validación
  // de confianza floja que no hace falta tener. Ahora la cache solo sirve
  // para decidir si vale la pena intentar restaurar sesión; la sesión real
  // se confirma pidiendo un access token nuevo vía la cookie httpOnly y
  // haciendo una llamada autenticada real antes de setear isAuthenticated.
  useEffect(() => {
    const init = async () => {
      const stored = await storage.getItem(STORAGE_KEY);
      let cached: StoredAuth | null = null;
      if (stored) {
        try {
          cached = JSON.parse(stored);
        } catch {
          cached = null;
        }
      }

      if (cached?.user && hasWebSessionCookie()) {
        try {
          await refreshSession();
          const tenants = await authRepository.getMyTenants();
          setState({
            user: cached.user,
            tenants,
            currentTenantId: cached.currentTenantId,
            isAuthenticated: true,
            isLoading: false,
            requiresTenantSelection: false,
          });
          return;
        } catch {
          // Cookie ausente/expirada o sesión inválida: no confiar en la
          // cache, tratar como no logueado.
        }
      }

      await storage.removeItem(STORAGE_KEY);
      setState({
        user: null,
        tenants: [],
        currentTenantId: null,
        isAuthenticated: false,
        isLoading: false,
        requiresTenantSelection: false,
      });
    };

    init();
  }, [storage, authRepository]);

  const saveToStorage = useCallback((data: StoredAuth) => {
    storage.setItem(STORAGE_KEY, JSON.stringify(data)).catch(() => {});
  }, [storage]);

  const clearStorage = useCallback(() => {
    storage.removeItem(STORAGE_KEY).catch(() => {});
  }, [storage]);

  const handleLoginResponse = useCallback((response: AdminLoginResponse) => {
    if (response.requiresTenantSelection && response.tenants) {
      setState((prev) => ({
        ...prev,
        user: response.user || null,
        tenants: response.tenants || [],
        isAuthenticated: false,
        isLoading: false,
        requiresTenantSelection: true,
      }));
    } else if (response.user) {
      const newState = {
        user: response.user,
        tenants: response.tenants || [],
        currentTenantId: response.user.currentTenantId || null,
      };
      saveToStorage(newState);
      setState({
        ...newState,
        isAuthenticated: true,
        isLoading: false,
        requiresTenantSelection: false,
      });
    }
  }, [saveToStorage]);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await authRepository.login({ email, password });
      handleLoginResponse(response);
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [authRepository, handleLoginResponse]);

  const logout = useCallback(async () => {
    try {
      await authRepository.logout();
    } finally {
      clearStorage();
      setState({
        user: null,
        tenants: [],
        currentTenantId: null,
        isAuthenticated: false,
        isLoading: false,
        requiresTenantSelection: false,
      });
    }
  }, [authRepository, clearStorage]);

  const selectTenant = useCallback(async (tenantId: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const selected = state.tenants.find((t) => t.tenantId === tenantId);
      const userId = selected?.userId ?? state.user?.id;
      if (!userId) {
        throw new Error('No se pudo resolver la cuenta para esta organización.');
      }
      const response = await authRepository.selectTenant({ tenantId, userId });
      handleLoginResponse(response);
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [authRepository, handleLoginResponse, state.tenants, state.user]);

  // Registra el acceso (SUPER_ADMIN_TENANT_ACCESS) sin tocar la sesión del portal.
  // ponytail: la app del tenant se abre sin sesión; el handoff a apps/web está pendiente.
  const accessTenant = useCallback(async (tenantId: string) => {
    await authRepository.accessTenant(tenantId);
  }, [authRepository]);

  const refreshAuth = useCallback(async () => {
    if (!state.user) return;

    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const tenants = await authRepository.getMyTenants();
      setState((prev) => ({
        ...prev,
        tenants,
        isLoading: false,
      }));
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [authRepository, state.user]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        selectTenant,
        accessTenant,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
